<template>
	<section ref="el" data-component="benefits">
		<div class="g-row">
			<div class="g-col xxl-16">
				<p class="-body-regular title">{{ title }}</p>
			</div>
		</div>
		<div ref="marqueeWrapper" class="marquee-wrapper">
			<div ref="marqueeTrack" class="marquee-track">
				<div ref="marqueeGroupA" class="marquee-group">
					<CardBenefit v-for="(card, k) in [...list, ...list]" :key="`a-${k}`" v-bind="card" />
				</div>
				<div ref="marqueeGroupB" class="marquee-group" aria-hidden="true">
					<CardBenefit v-for="(card, k) in [...list, ...list]" :key="`b-${k}`" v-bind="card" />
				</div>
			</div>
		</div>
	</section>
</template>

<script setup>
import { gsap, ScrollTrigger } from 'gsap/all'

import { CardBenefit } from '~/components/fragments'

import { useScreen } from 'vue-screen'
import { useClientOnlyComputed } from '~/composables/core'
import { breakpoints } from '~/config'

import.meta.client && gsap.registerPlugin(ScrollTrigger)

const props = defineProps({
	title: { type: String, default: undefined },
	list: { type: Array, default: undefined },
	marqueeSpeed: { type: Number, default: 40 },
})

const el = ref(null)
const marqueeWrapper = ref(null)
const marqueeTrack = ref(null)
const marqueeGroupA = ref(null)
const marqueeGroupB = ref(null)
const context = inject('pageContext')
const screen = useScreen()

const isMobile = useClientOnlyComputed(true, () => screen.orientation === 'portrait' && screen.width <= breakpoints.xsmall, screen)

const marqueeSpeed = ref(Math.max(1, Number(props.marqueeSpeed) || 120))

let marqueeTween = null
let marqueeTrigger = null
let resizeObserver = null
let rebuildRaf = null
let cycleDistance = 0

const setMarqueeSpeed = (nextSpeed) => {
	const parsed = Number(nextSpeed)
	if (!Number.isFinite(parsed) || parsed <= 0) return
	marqueeSpeed.value = parsed
	if (marqueeTween && cycleDistance > 0) {
		marqueeTween.duration(cycleDistance / marqueeSpeed.value)
	}
}

const buildMarquee = () => {
	if (!marqueeTrack.value || !marqueeGroupA.value || !marqueeGroupB.value || !props.list?.length) return

	const nextCycleDistance = marqueeGroupB.value.offsetLeft - marqueeGroupA.value.offsetLeft
	if (!nextCycleDistance) return

	cycleDistance = nextCycleDistance

	marqueeTween?.kill()
	gsap.set(marqueeTrack.value, { x: 0 })

	marqueeTween = gsap.to(marqueeTrack.value, {
		x: `-=${cycleDistance}`,
		duration: cycleDistance / marqueeSpeed.value,
		ease: 'none',
		repeat: -1,
		paused: true,
		modifiers: {
			x: gsap.utils.unitize((x) => gsap.utils.wrap(-cycleDistance, 0, parseFloat(x))),
		},
	})
}

const setup = () => {
	buildMarquee()

	marqueeTrigger?.kill()
	marqueeTrigger = ScrollTrigger.create({
		trigger: el.value,
		start: 'top bottom',
		end: 'bottom top',
		onEnter: () => marqueeTween?.play(),
		onEnterBack: () => marqueeTween?.play(),
		onLeave: () => marqueeTween?.pause(),
		onLeaveBack: () => marqueeTween?.pause(),
	})

	if (marqueeTrigger.isActive) {
		marqueeTween?.play()
	}

	if (marqueeWrapper.value) {
		resizeObserver = new ResizeObserver(() => {
			if (rebuildRaf) cancelAnimationFrame(rebuildRaf)
			rebuildRaf = requestAnimationFrame(() => {
				const shouldPlay = !!marqueeTrigger?.isActive
				buildMarquee()
				if (shouldPlay) marqueeTween?.play()
			})
		})
		resizeObserver.observe(marqueeWrapper.value)
		marqueeTrack.value && resizeObserver.observe(marqueeTrack.value)
		marqueeGroupA.value && resizeObserver.observe(marqueeGroupA.value)
	}
}

const init = () => {
	if (marqueeTrigger?.isActive) marqueeTween?.play()
}

const kill = () => {
	if (rebuildRaf) {
		cancelAnimationFrame(rebuildRaf)
		rebuildRaf = null
	}
	resizeObserver?.disconnect()
	resizeObserver = null
	marqueeTrigger?.kill()
	marqueeTrigger = null
	marqueeTween?.kill()
	marqueeTween = null
}

watch(
	() => props.marqueeSpeed,
	(nextSpeed) => {
		setMarqueeSpeed(nextSpeed)
	}
)

watch(
	() => props.list?.length,
	async () => {
		await nextTick()
		const shouldPlay = !!marqueeTrigger?.isActive
		buildMarquee()
		if (shouldPlay) marqueeTween?.play()
	}
)

defineExpose({
	marqueeSpeed,
	setMarqueeSpeed,
})

onBeforeUnmount(() => kill())
onMounted(() => {
	context.$page.loader.loaded.then(() => setup())
	context.$page.loader.transitionReady.then(() => init())
})
</script>

<style lang="scss" scoped>
[data-component='benefits'] {
	position: relative;
	--marquee-gap: #{rs(12, 10)};

	display: flex;
	flex-direction: column;

	width: 100%;
	margin-top: rs(72, 56);

	.title {
		color: var(--color-neutral-tints-400);
	}

	.marquee-wrapper {
		width: 100vw;
		overflow: hidden;
		margin-top: rs(12, 10);
	}

	.marquee-track {
		display: flex;
		flex-direction: row;
		flex-wrap: nowrap;
		gap: var(--marquee-gap);
		width: max-content;
		will-change: transform;
	}

	.marquee-group {
		display: flex;
		flex-direction: row;
		flex-wrap: nowrap;
		gap: var(--marquee-gap);
	}
}
</style>
